import classNames from 'classnames';
import ProcessPage from 'components/ProcessPage';
import injectSaga from 'utils/injectSaga';
import injectReducer from 'utils/injectReducer';
import AHref from 'components/AHref';
import Verification from 'components/Verification';
import slugify from "react-slugify";
import { makeSelectUser } from 'containers/App/selectors';
import { selectWorkspaceRequest, selectWorkspaceSsoLoginRequest } from 'containers/Login/actions';
import reducer from 'containers/Login/reducer';
import saga from 'containers/Login/saga';
import { makeworkspaceSetupInvited } from 'containers/Login/selectors';
import { fetchUserWorkspaceRequest } from 'containers/Settings/Workspace/actions';
import { updateWorkspaceFavourite } from 'api/auth';
import DeactivatedWorkspace from 'containers/Settings/Workspace/DeactivatedWorkspace';
import {
  makeSelectLoading, makeSelectUserWorkspace,
} from 'containers/Settings/Workspace/selectors';
import { PlusXsIcon, SearchIcon, CloseIcon } from 'images/common';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { routeMapping } from 'routes';
import appMessages from 'utils/messages';
import Modal from 'components/Modal';
import SuspendedWorkspace from './SuspendedWorkspace';
import { withRouter } from 'withRouter';
import { isEmpty } from 'lodash';
import { defaultNotifier } from 'functions/notificationHandler';
import { GoogleOAuthProvider } from '@react-oauth/google/dist';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { VuiRowIconList, VuiTooltip } from '@topkamera/viso-ui';
import SsoPage from 'containers/Auth/SSO/SsoPage';
import VerificationCode from 'containers/Settings/User/Detail/components/VerificationCode';
import './Select.scss';

function MicrosoftLoginButton({ workspace, handleLoginResponse = () => null, children }) {
  const clientId = workspace?.sso?.configuration?.applicationId;
  const tenantId = workspace?.sso?.configuration?.tenantId;
  const redirectUri = `${window.location.origin}/workspace-sso/microsoft`;
  localStorage.setItem('workspaceSubdomain', workspace.subDomain);

  const handleLogin = async () => {
    if (!clientId) {
      return defaultNotifier('Workspace Microsoft SSO client ID is not configured');
    }
    if (!tenantId) {
      return defaultNotifier('Workspace Microsoft SSO tenant ID is not configured');
    }

    try {
      const genRandomString = (len = 32) => {
        const arr = new Uint8Array(len);
        window.crypto.getRandomValues(arr);
        return Array.from(arr, (b) => ('0' + (b & 0xff).toString(16)).slice(-2)).join('');
      }
      const base64url = (buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      }
      const generateCodeVerifier = () => {
        const random = new Uint8Array(32);
        window.crypto.getRandomValues(random);
        return base64url(random);
      }
      const computeCodeChallenge = async (verifier) => {
        const data = new TextEncoder().encode(verifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return base64url(digest);
      }

      const state = genRandomString(16);
      const nonce = genRandomString(16);
      sessionStorage.setItem('ms_sso_state', state);
      sessionStorage.setItem('ms_sso_nonce', nonce);

      const codeVerifier = generateCodeVerifier();
      sessionStorage.setItem('ms_pkce_verifier', codeVerifier);

      const codeChallenge = await computeCodeChallenge(codeVerifier);

      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`;
      const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        redirect_uri: redirectUri,
        scope: 'openid profile offline_access',
        response_mode: 'query',
        prompt: 'select_account',
        state,
        nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });
      window.location.assign(`${authUrl}?${params.toString()}`);      
      return
    } catch (err) {
      defaultNotifier("Workspace Microsoft SSO login failed")
      console.error("Workspace Microsoft SSO login failed", err)
    }
  }

  const safeChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onClick: (e) => {
          e.preventDefault()
          e.stopPropagation()
          handleLogin()
        },
      })
    }
    return child
  })

  return <button className='width-webkit-fill' onClick={handleLogin}>{safeChildren}</button>
}

class SelectWorkspace extends Component {
  constructor(props) {
    super(props)
    this.textInput = React.createRef();
  }

  state = {
    showWarning: false,
    activeWs: '',
    codes: [],
    activeIndex: 0,
    type: 'code',
    showAuthenticationModal: false,
    searchValue: '',
    filteredWorkspaces: null,
    workspacesFavourites: {}
  };

  onChange = (e, index) => {
    const value = e.key || e.currentTarget.value;

    this.state.codes[index] = value;
    this.setState({ activeIndex: parseInt(index, 10) + 1 });
    this.setState({ codes: this.state.codes });
  }

  onUpdate = value => this.setState({ codes: value })

  async componentDidMount() {
    if (this.props.user && this.props.user.passwordResetRequired) {
      return this.props.router.navigate(routeMapping.CHANGE_PASSWORD.path);
    }

    const workspacesProp = Array.isArray(this.props.workspaces) ? this.props.workspaces : []
    const userWorkspaces = (this.props.user && Array.isArray(this.props.user.workspaces)) ? this.props.user.workspaces : []
    const currentWorkspaces = (workspacesProp.length && workspacesProp) || userWorkspaces || []
    if (isEmpty(this.state.workspacesFavourites) && !isEmpty(currentWorkspaces)) {
      const workspaceFavourites = {}
      currentWorkspaces.forEach(ws => {
        workspaceFavourites[ws.id] = ws.isFavourite
      })
      this.setState({ workspacesFavourites: workspaceFavourites })
    }
  }

  onClose = () => {
    const {
      user: { activeWorkspace },
    } = this.props;

    if (activeWorkspace) {
      return this.props.router.navigate(-1);
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');

    this.props.router.navigate(routeMapping.LOGIN.path);
  };

  onCloseModal = () => {
    this.setState({ showAuthenticationModal: false });
  }

  onUpdateOption = () => {
    const activeType = this.state.type;

    if (activeType === 'code') {
      return this.setState({ type: 'recoveryKey' });
    }
    return this.setState({ type: 'code' });
  }

  renderModal = () => {
    const { codes } = this.state;
    const code = codes.length && codes.reduce((sum, item) => `${sum}${item}`);

    const buttons = [
      <button
        disabled={code.length !== 6}
        className="modal__confirm label-color button_xs button_no_padding"
        key="confirm"
        onClick={() => {
          if (code.length === 6) {
            this.onSubmitCode(this.state.codes, this.state.type);
          }
        }}
      >
        Verify
      </button>,
    ];

    return (
      <Modal
        buttons={buttons}
        title={this.state.type === 'code' ? 'Verification Code' : 'Recovery Key'}
        body={(
          <div>
            Please enter the 6-digit security code from your mobile phone app to continue. Or use the
            {' '}
            <AHref onClick={this.onUpdateOption}>{this.state.type === 'code' ? 'recovery key' : 'verification code'}</AHref>
            .
            <br />
            <br />
            <Verification
              codes={this.state.codes}
              onUpdate={this.onUpdate}
              activeIndex={this.state.activeIndex}
              onChange={this.onChange}
            />
          </div>
        )}
        onClose={this.onCloseModal}
      />
    );
  };

  onCreate = () => {
    this.props.router.navigate(routeMapping.NEW_WORKSPACE.path);
  };

  onCreateDev = () => {
    this.props.router.navigate(
      `${routeMapping.NEW_WORKSPACE.path}?isCreatePage=true`
    );
  };

  onSubmitCode = (codes, type) => {
    const code = codes.reduce((sum, item) => `${sum}${item}`);

    let payload = this.state.wsData;

    if (type === 'code') {
      payload = {
        ...this.state.wsData,
        twoFactorCode: code,
      };
    }

    if (type === 'recoveryKey') {
      payload = {
        ...this.state.wsData,
        twoFactorRecoveryKey: code,
      };
    }

    this.setState({ showVerificationCode: false });
    return this.props.selectWorkspaceRequest(payload, {
      push: this.props.router.navigate,
      onCallBack: () => this.onCloseModal(),
    });
  }

  getWorkspaceStatus = (status) => {
    if (status === 'locked') {
      return 'Locked'
    }

    if (status === 'inactive') {
      return 'Suspended'
    }

    return status
  }

  getWorkspaceStatusTooltip = (status) => {
    if (status === 'locked') {
      return 'The workspace is locked. Please contact support.'
    }

    if (status === 'inactive') {
      return 'The workspace is suspended. Please contact support.'
    }

    if (status === 'deleted') {
      return 'The workspace has been deleted. Please contact support.'
    }

    return status
  }

  onSelectWorkspace = (data) => {
    const { workspaces = [] } = this.props.user;

    const args = {
      subDomain: data,
    };
    const workspace = workspaces.find(item => item.subDomain === data);

    if (workspace && workspace.status === 'inactive' && workspace.userRole !== 'Owner') {
      return this.setState({ selectedWs: workspace, showWarning: true });
    }

    if (workspace && workspace.status === 'inactive' && workspace.userRole === 'Owner') {
      return this.setState({ selectedWs: workspace, isSuspended: true });
    }

    if (
      (workspace && workspace.userRole === 'Owner')
      || (workspace && workspace.status === 'active')
    ) {
      if (workspace.twoFactorEnabled) {
        this.setState({ showAuthenticationModal: true });
        return this.setState({ wsData: args });
      }
      this.setState({ showWarning: false });
      return this.props.selectWorkspaceRequest(args, {
        push: this.props.router.navigate,
      });
    }

    this.setState({ selectedWs: workspace, showWarning: true });
  };

  workspaceSsoLogin = (workspace, response) => {
    const { sso = {} } = workspace

    if (!isEmpty(response) && workspace.sso?.ssoType === 'google') {
      const payload = {
        sso: sso,
        token: response.code,
        subDomain: workspace.subDomain
      }
      return this.props.selectWorkspaceSsoLoginRequest(payload, {
        push: this.props.router.navigate,
        // onCallBack: () => this.onCloseModal(),
      });
    }

    if (!isEmpty(response) && workspace.sso?.ssoType === 'microsoft') {
      const payload = {
        sso: sso,
        token: response.accessToken,
        // token: response.idToken,
        subDomain: workspace.subDomain
      }
      return this.props.selectWorkspaceSsoLoginRequest(payload, {
        push: this.props.router.navigate,
        // onCallBack: () => this.onCloseModal(),
      });
    }
  }

  handleWorkspaceClick = async (workspace) => {
    const params = new URLSearchParams(window.location.search);
    const ssoClient = params.get('ssoClient');

    if (workspace?.sso?.configuration?.clientId && workspace.sso?.ssoType === 'google' && ssoClient) {
      if (!window.google) {
        return defaultNotifier("Google SDK is not loaded yet");
      }

      localStorage.setItem('workspaceSubdomain', workspace.subDomain);

      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: workspace.sso.configuration.clientId,
        scope: "openid email profile",
        ux_mode: 'redirect',
        redirect_uri: `${window.location.origin}/workspace-sso/google`,
        callback: (response) => this.workspaceSsoLogin(workspace, response),
      });

      const response = await client.requestCode()
      return this.workspaceSsoLogin(workspace, response)
    }

    if (workspace?.sso?.configuration?.applicationId && workspace.sso?.ssoType === 'microsoft' && ssoClient) {
      const clientId = workspace?.sso?.configuration?.applicationId;
      const tenantId = workspace?.sso?.configuration?.tenantId;
      const redirectUri = `${window.location.origin}/workspace-sso/microsoft`;
      localStorage.setItem('workspaceSubdomain', workspace.subDomain);

      if (!clientId) {
        return defaultNotifier('Workspace Microsoft SSO client ID is not configured');
      }
      if (!tenantId) {
        return defaultNotifier('Workspace Microsoft SSO tenant ID is not configured');
      }

      try {
        const genRandomString = (len = 32) => {
          const arr = new Uint8Array(len);
          window.crypto.getRandomValues(arr);
          return Array.from(arr, (b) => ('0' + (b & 0xff).toString(16)).slice(-2)).join('');
        }
        const base64url = (buffer) => {
          let binary = '';
          const bytes = new Uint8Array(buffer);
          for (let i = 0; i < bytes.byteLength; i += 1) {
            binary += String.fromCharCode(bytes[i]);
          }
          return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        }
        const generateCodeVerifier = () => {
          const random = new Uint8Array(32);
          window.crypto.getRandomValues(random);
          return base64url(random);
        }
        const computeCodeChallenge = async (verifier) => {
          const data = new TextEncoder().encode(verifier);
          const digest = await window.crypto.subtle.digest('SHA-256', data);
          return base64url(digest);
        }

        const state = genRandomString(16);
        const nonce = genRandomString(16);
        sessionStorage.setItem('ms_sso_state', state);
        sessionStorage.setItem('ms_sso_nonce', nonce);

        const codeVerifier = generateCodeVerifier();
        sessionStorage.setItem('ms_pkce_verifier', codeVerifier);

        const codeChallenge = await computeCodeChallenge(codeVerifier);

        const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`;
        const params = new URLSearchParams({
          client_id: clientId,
          response_type: 'code',
          redirect_uri: redirectUri,
          scope: 'openid profile offline_access',
          response_mode: 'query',
          prompt: 'select_account',
          state,
          nonce,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
        });
        window.location.assign(`${authUrl}?${params.toString()}`);      
        return
      } catch (err) {
        defaultNotifier("Workspace Microsoft SSO login failed")
        console.error("Workspace Microsoft SSO login failed", err)
        return
      }
    }

    this.onSelectWorkspace(workspace.subDomain)
  }

  onTouchWs = (ws) => {
    this.setState({ activeWs: ws });
  };

  onSearch = (event) => {
    const { workspaces = [], user } = this.props;
    const searchValue = event.target.value;

    const workspaceList = (!!workspaces.length && workspaces) || user.workspaces || [];
    const filteredWorkspaces = workspaceList
      .filter(wk => wk
        .name.toLowerCase().includes(searchValue.toLowerCase()));

    this.setState({ filteredWorkspaces: filteredWorkspaces, searchValue: searchValue });
  }

  onClear = () => {
    this.textInput.current.value = '';
    this.setState({ searchValue: '', filteredWorkspaces: null });
  }

  onFavoriteWorkspace = async (workspace, newValue) => {
    try {
      const { workspacesFavourites } = this.state
      await updateWorkspaceFavourite({ workspaceId: workspace?.id, isFavourite: newValue });
      this.setState({
        workspacesFavourites: {
          ...workspacesFavourites,
          [workspace.id]: newValue
        }
      })
    } catch (error) {
      defaultNotifier(`Error selecting ${workspace.name} as favourite`)
    }
  }

  getWorkspaceList = () => {
    const { filteredWorkspaces, workspacesFavourites } = this.state;
    const { workspaces = [], user } = this.props;

    const workspaceList = filteredWorkspaces || (!!workspaces.length && workspaces) || user.workspaces || [];

    return [...workspaceList].sort((a, b) => (workspacesFavourites[b.id] ? 1 : 0) - (workspacesFavourites[a.id] ? 1 : 0))
  }

  renderWorkspaceItem = (workspace) => {
    const buildCaptions = [
      ...workspace.userRole ? [workspace.userRole] : []
    ]
    const isFavourite = this.state.workspacesFavourites[workspace.id]
    const isNew = workspace.isNew
    const isInActive = workspace.status !== 'active'
    const alertIndicator = {
      borderStyle: 'fine',
      color: 'alert',
      dataTooltipContent: this.getWorkspaceStatusTooltip(workspace.status),
      dataTooltipId: 'selectWorkspaceTooltip',
      icon: 'exclamation',
      size: 'lg'
    }
    const newIndicator = {
      label: 'NEW',
      color: 'success'
    }

    const workspaceRowItem = (
      <div key={workspace.subDomain}>
        <VuiRowIconList
          action={!isInActive && {
            color: isFavourite ? 'accent0' : 'border',
            iconLeft: 'star',
            onClick: () => this.onFavoriteWorkspace(workspace, !isFavourite),
            style: 'color'
          }}
          color='accent0'
          gap={0}
          iconSize='md'
          items={[
            {
              captions: buildCaptions,
              icon: !(workspace && workspace.logo) && 'workspace',
              image: workspace && workspace.logo,
              iconIndicator: isInActive ? alertIndicator : isNew && newIndicator,
              isActive: isFavourite,
              text: workspace.name || workspace.subDomain || workspace.organizationName
            }
          ]}
          onClick={!isInActive ?
            (data, e) => {
              e.stopPropagation()
              this.handleWorkspaceClick(workspace)
            } :
            undefined
          }
        />
      </div>
    )

    const params = new URLSearchParams(window.location.search);
    const ssoClient = params.get('ssoClient');
    
    if (workspace && workspace.sso && ssoClient) {
      if (workspace.sso.ssoType === 'google') {
        return (
          <GoogleOAuthProvider clientId={workspace?.sso?.configuration?.clientId}>
            {workspaceRowItem}
          </GoogleOAuthProvider>
        )
      }
      if (workspace.sso.ssoType === 'microsoft') {
        return (
          <MicrosoftLoginButton workspace={workspace} handleLoginResponse={(response) => this.workspaceSsoLogin(workspace, response)}>
            {workspaceRowItem}
          </MicrosoftLoginButton>
        )
        // const msalConfig = {
        //   auth: {
        //     clientId: `${workspace?.sso?.configuration?.applicationId}`,
        //     authority: `https://login.microsoftonline.com/${workspace?.sso?.configuration?.tenantId}/v2.0`,
        //     redirectUri: `${workspace?.sso?.configuration?.redirectedURI}`
        //     ,
        //   },
        // }
        // const msalInstance = new PublicClientApplication(msalConfig)

        // return (
        //   <MsalProvider instance={msalInstance}>
        //     <MicrosoftLoginButton workspace={workspace} handleLoginResponse={(response) => this.workspaceSsoLogin(workspace, response)}>
        //       {workspaceRowItem}
        //     </MicrosoftLoginButton>
        //   </MsalProvider>
        // )
      }
    }

    return workspaceRowItem
  }

  render() {
    const { searchValue } = this.state;
    const { user } = this.props;

    const workspaceList = this.getWorkspaceList()

    if (this.state.showWarning) {
      return (
        <DeactivatedWorkspace
          workspace={this.state.selectedWs}
          onClose={this.onClose}
        />
      );
    }

    if (this.state.isSuspended) {
      return (
        <SuspendedWorkspace
          workspace={this.state.selectedWs}
          onClose={this.onClose}
        />
      );
    }

    if (this.state.showAuthenticationModal) {
      return (
        <VerificationCode onSubmit={this.onSubmitCode} />
      )
    }

    return (
      <ProcessPage onCancel={this.onClose} splitPage={true}>
        {() => (
          <div>
            <SsoPage
              title='Choose a workspace'
              description='Search or pick from the list below.'
            >
              <div className="select-workspace">
                <div className={`select-workspace__search text-field text-field_nolabel`}>
                  <input
                    type="text"
                    placeholder="Search workspaces"
                    className={classNames("text-field__input", "text-field__input--no-border mx-0")}
                    onChange={this.onSearch}
                    ref={this.textInput}
                  />
                  <div className="custom-search-text-field__input" />
                  <div className="text-field__underscore" />
                  <div className="text-field__icon">{!searchValue && <SearchIcon />}</div>
                  {searchValue && <span className={classNames('cursor', { 'text-field__closeIcon': searchValue })} onClick={() => this.onClear()}><CloseIcon /></span>}
                </div>
                {!workspaceList.length && (
                  <div className="viso-text-label vui-text-center">
                    No workspaces available.
                  </div>
                )}
                {workspaceList && workspaceList.map((workspace, index) => {
                  if (workspace) {
                    return (
                      <div key={index}>
                        {this.renderWorkspaceItem(workspace)}
                      </div>
                    )
                  }
                  return <span key={index} />
                })}
                {user.workspaceSetupInvited && (
                  <>
                    <a
                      href="javascript:void(0)"
                      className="workspace-select__item"
                      onClick={this.onCreateDev}
                    >
                      <div className="checkbox relative">
                        <div className="card__new">
                          <FormattedMessage {...appMessages.cNew} />
                        </div>
                        <div className="checkbox__image">
                          <PlusXsIcon />
                        </div>
                      </div>
                      <div className="workspace-select__label h3">
                        Set up your workspace
                      </div>
                    </a>
                  </>
                )}
              </div>
            </SsoPage>
            <VuiTooltip tooltipId='selectWorkspaceTooltip' />
          </div>
        )}
      </ProcessPage>
    );
  }
}

SelectWorkspace.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  workspaces: PropTypes.any,
  selectWorkspaceRequest: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  user: makeSelectUser(),
  isLoading: makeSelectLoading(),
  workspaceSetupInvited: makeworkspaceSetupInvited(),
  workspaces: makeSelectUserWorkspace(),
  userData: makeSelectUser(),
});

const withConnect = connect(
  mapStateToProps,
  { selectWorkspaceRequest, fetchUserWorkspaceRequest, selectWorkspaceSsoLoginRequest },
);

const withReducer = injectReducer({ key: 'login', reducer });
const withSaga = injectSaga({ key: 'login', saga });

export default compose(
  withRouter,
  withReducer,
  withSaga,
  withConnect,
)(SelectWorkspace);
